import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import ChartExportAsPNG from "../../../../Utils/ChartExportAsPng";

const PieChart = ({ data, totalValue, containerClassName, exportRef, selectedCity, datasetName, year }) => {
  const svgRef = useRef();
  const [excludedLegends, setExcludedLegends] = useState([]);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const prevPieDataRef = useRef({});
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });


  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect;
        setDimensions({
          width: width,
          height: width,
        });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const handleLegendClick = (name) => {
    setIsFirstRender(false);

    if (!excludedLegends.includes(name)) {
      setExcludedLegends((prev) => [...prev, name]);
    } else {
      const filtered = excludedLegends.filter((legend) => legend !== name);
      setExcludedLegends(filtered);
    }
  };

  const filteredData = data.filter((d) => !excludedLegends.includes(d.name));

  useEffect(() => {
    const { width, height } = dimensions;
    d3.select(svgRef.current).style("pointer-events", "none");
    d3.select("body").selectAll(".tooltip").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
      .style("width", "100%")
      .style("height", "auto")
      .style("pointer-events", "none");

    svg.selectAll("*").remove();

    const radius = width / 2 - 5;

    const pie = d3
      .pie()
      .value((d) => d.value / totalValue)
      .sort(null);

    const arc = d3.arc().innerRadius(0).outerRadius(radius);

    const g = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")

      .style("background", "#000")
      .style("color", "#fff")
      .style("padding", "5px 10px")
      .style("border-radius", "5px")
      .style("font-size", "12px")
      .style("opacity", 0.8);

    const pieData = pie(filteredData);

    const key = (d) => d.data.name;



    const arcs = g
      .selectAll("path")
      .data(pieData, key)
      .join(
        (enter) =>
          enter
            .append("path")
            .attr("fill", (d) => d.data.color)
            .attr("stroke", "white")
            .attr("stroke-width", 2)
            .each(function (d) {
              if (isFirstRender) {
                this._current = { startAngle: 0, endAngle: 0 };
              } else {
                const prevData = prevPieDataRef.current[d.data.name];
                if (prevData) {
                  this._current = prevData;
                } else {
                  this._current = {
                    startAngle: d.startAngle,
                    endAngle: d.startAngle,
                  };
                }
              }
            })
            .call((enter) =>
              enter
                .transition()
                .duration(1000)
                .attrTween("d", function (d) {
                  const i = d3.interpolate(this._current, d);

                  return (t) => arc(i(t));
                })
            ),

        (update) =>
          update.call((update) =>
            update
              .transition()
              .duration(500)
              .attrTween("d", function (d) {
                const i = d3.interpolate(this._current, d);

                return (t) => arc(i(t));
              })
          ),

        (exit) =>
          exit.call((exit) =>
            exit
              .transition()
              .duration(500)
              .attrTween("d", function (d) {
                prevPieDataRef.current[d.data.name] = this._current;

                const end = {
                  ...d,
                  startAngle: d.startAngle,
                  endAngle: d.startAngle,
                };
                const i = d3.interpolate(this._current, end);
                return (t) => arc(i(t));
              })
              .remove()
          )
      )

      .on("mouseover", (event, d) => {
        tooltip
          .style("opacity", 1)
          .html(
            `<p style="margin:0;">
       <span style="display:inline-flex; align-items:center; gap:6px;">
        <span style="width:14px; height:14px; background-color:${d.data.color
            }; border:1px solid rgba(255,255,255,0.7); display:inline-block;"></span>
                      <span">${d.data.name}: ${(
              (d.data.value / totalValue) *
              100
            ).toFixed(2)}%</span>
                    </span>
                  </p>`
          )
          .style("left", `${event.pageX + 10}px`)
          .style("top", `${event.pageY - 28}px`);
        d3.select(event.currentTarget).attr(
          "fill",
          d3.rgb(d.data.color).darker(0.5)
        );
      })
      .on("mousemove", (event) => {
        tooltip
          .style("left", `${event.pageX + 10}px`)
          .style("top", `${event.pageY - 28}px`);
      })
      .on("mouseout", (event, d) => {
        tooltip.style("opacity", 0);
        d3.select(event.currentTarget).attr("fill", d.data.color);
      });

    pieData.forEach((d) => {
      prevPieDataRef.current[d.data.name] = d;
    });

    arcs
      .transition()
      .duration(1000)
      .attrTween("d", function (d) {
        let i;
        if (isFirstRender) {
          i = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
        } else {
          i = d3.interpolate(this._current, d);
          this._current = i(0);
        }

        return function (t) {
          return arc(i(t));
        };
      })
      .on("end", () => {
        svg.style("pointer-events", "all");
      });
  }, [data, totalValue, filteredData, isFirstRender, dimensions]);

  return (
    <div className="w-full flex flex-col xl:flex-row justify-center items-center gap-3  xl:gap-20" >
      <div
        ref={containerRef}
        style={{ width: "100%" }}
        className={containerClassName}
      >
        <div id="chart-page-pie-chart">
          <svg ref={svgRef} ></svg>
        </div>

      </div>

      <div className="flex flex-wrap xl:flex-col  gap-1 md:gap-2 lg:gap-3 xl:gap-1 justify-center">
        {data.map((d, idx) => (
          <>
            <div className="flex items-center  gap-1 md:gap-2 lg:gap-3  text-start " key={idx} onClick={() => handleLegendClick(d.name)}>
              <button
                style={{ backgroundColor: d.color }}
                className="w-7 h-3 flex-shrink-0"
                aria-label={d.name}
              />
              <button
                title={d.name}
                className={`${excludedLegends.includes(d.name)
                  ? "line-through"
                  : "no-underline"
                  } text-gray-500 cursor-pointer text-sm sm:text-base truncate`}

              >
                {d.name}
              </button>
            </div>

           <ChartExportAsPNG
              ref={exportRef}
              targetId="chart-page-pie-chart"
              targetWidth={250}
              fileNamePrefix={datasetName + ' Pie Chart ' + selectedCity.name}
              chartData={data}
              imageTitle={datasetName + ' - Pie Chart'}
              imageSource={`${selectedCity.name} ${(selectedCity.type).charAt(0).toUpperCase() + (selectedCity.type).slice(1).toLowerCase()} - ${year}`}
              LegendFirstColumn={datasetName}
              titleFont="bold 20px Arial"
              sourceFont="italic 18px Arial"
            />
          </>
        ))}
      </div>
    </div>
  );
};

export default PieChart;
